import React, { useState } from "react"
import { Form, Button } from "react-bootstrap"
import DropdownControl from "./DropdownControl"
import CDropdown from "./CDropdown"
import { ReactComponent as CloseIcon } from "../svgs/close.svg"

interface UserSearchFilterProps {
    searchQuery: string
    filters: {
        branch?: string
        app?: string
        com?: string
    }
    branches: Array<{ id: string; name: string }> | null | undefined
    apps: Array<{ id?: string; name: string }> | null | undefined
    // Populated only for core1 admins while cross-company browsing is enabled.
    companies?: Array<{ id?: string; name?: string }> | null
    onSearchChange: (query: string) => void
    onFiltersChange: (filters: { branch?: string; app?: string; com?: string }) => void
    onClearFilters: () => void
}

const UserSearchFilter: React.FC<UserSearchFilterProps> = ({
    searchQuery,
    filters,
    branches,
    apps,
    companies,
    onSearchChange,
    onFiltersChange,
    onClearFilters
}) => {
    const [localSearchQuery, setLocalSearchQuery] = useState(searchQuery)
    const [searchTimer, setSearchTimer] = useState<NodeJS.Timeout | null>(null)
    
    // Calculate selected indices for dropdowns
    const branchIndex = !filters.branch ? 0 : (branches?.findIndex(b => b.id === filters.branch) ?? -1) + 1
    const appIndex = !filters.app ? 0 : (apps?.findIndex(a => a.id === filters.app) ?? -1) + 1
    const comIndex = !filters.com ? 0 : (companies?.findIndex(c => c.id === filters.com) ?? -1) + 1
    
    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value
        setLocalSearchQuery(value)
        
        // Clear existing timer
        if (searchTimer) {
            clearTimeout(searchTimer)
        }
        
        // If empty, reset immediately
        if (value === "") {
            onSearchChange("")
            return
        }
        
        // Set new timer for debouncing
        const timer = setTimeout(() => {
            onSearchChange(value)
        }, 1000)
        setSearchTimer(timer)
    }
    
    const handleSearchKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            // Clear timer and search immediately
            if (searchTimer) {
                clearTimeout(searchTimer)
            }
            onSearchChange(localSearchQuery)
        }
    }
    
    const handleBranchFilter = (key: string | null, index: number) => {
        const branch = index === 0 || !branches ? undefined : branches[index - 1].id
        onFiltersChange({ ...filters, branch })
    }
    
    const handleAppFilter = (key: string | null, index: number) => {
        const app = index === 0 || !apps ? undefined : apps[index - 1].id
        onFiltersChange({ ...filters, app })
    }
    
    const handleCompanyFilter = (key: string | null, index: number) => {
        // Switching the company invalidates the branch filter: branches belong to a
        // company, so carrying one across would silently match nothing.
        const com = index === 0 || !companies ? undefined : companies[index - 1].id
        onFiltersChange({ ...filters, com, branch: undefined })
    }

    const handleClearFilters = () => {
        setLocalSearchQuery("")
        onClearFilters()
    }
    
    // Check if any filters are active
    const hasActiveFilters = searchQuery || filters.branch || filters.app || filters.com
    
    // Build dropdown items
    const branchItems = ["All Branches", ...(branches || []).map(b => b.name)]
    const appItems = ["All Apps", ...(apps || []).map(a => a.name)]
    const comItems = ["My Company", ...(companies || []).map(c => c.name || c.id || "")]
    
    return (
        <div style={{ 
            display: "flex", 
            gap: "12px", 
            alignItems: "center",
            marginBottom: "1rem"
        }}>
            <Form.Control 
                type="text"
                placeholder="Search by name or email..."
                value={localSearchQuery}
                onChange={handleSearchChange}
                onKeyPress={handleSearchKeyPress}
                style={{ flex: "1 1 40%" }}
            />
            {companies && companies.length > 0 && (
                <div style={{ flex: "0 0 auto", minWidth: "120px" }}>
                    <CDropdown
                        items={comItems}
                        selected={comIndex}
                        onSelected={handleCompanyFilter}
                    />
                </div>
            )}
            <div style={{ flex: "0 0 auto", minWidth: "100px" }}>
                <CDropdown
                    items={branchItems}
                    selected={branchIndex}
                    onSelected={handleBranchFilter}
                />
            </div>
            <div style={{ flex: "0 0 auto", minWidth: "100px" }}>
                <CDropdown
                    items={appItems}
                    selected={appIndex}
                    onSelected={handleAppFilter}
                />
            </div>
            {hasActiveFilters && (
                <Button 
                    variant="" 
                    onClick={handleClearFilters}
                    style={{ 
                        width: "40px", 
                        height: "40px", 
                        padding: "0",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0
                    }}
                    title="Clear filters"
                >
                    <CloseIcon style={{ stroke: "gray", width: "18px", height: "18px" }} />
                </Button>
            )}
        </div>
    )
}

export default UserSearchFilter